import { Loader2, X } from 'lucide-react';

/**
 * ConfirmModal — generic confirmation dialog
 *
 * Props:
 *  isOpen        {boolean}
 *  onClose       {() => void}
 *  onConfirm     {() => void}
 *  title         {string}
 *  description   {ReactNode}
 *  confirmLabel  {string}
 *  confirmClass  {string}   — tailwind classes for confirm btn background/hover
 *  cancelLabel   {string}   — defaults to "Cancel"
 *  loading       {boolean}  — shows spinner & disables confirm btn
 *  icon          {ReactNode} — the SVG icon inside the circle
 *  iconBg        {string}   — tailwind bg class for icon circle
 */
const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirm',
  confirmClass = 'bg-indigo-600 hover:bg-indigo-700',
  cancelLabel = 'Cancel',
  loading = false,
  icon,
  iconBg = 'bg-indigo-50',
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-[320px] p-6 flex flex-col items-center gap-3">
        {/* Icon */}
        <div className={`w-14 h-14 rounded-full flex items-center justify-center ${iconBg}`}>
          {icon}
        </div>

        {/* Title */}
        <h2 className="text-sm font-bold text-gray-800 text-center">{title}</h2>

        {/* Description */}
        <p className="text-xs text-gray-500 text-center leading-relaxed">{description}</p>

        {/* Buttons */}
        <div className="flex gap-3 w-full mt-1">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 py-1.5 text-xs border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50 font-medium transition-colors disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`flex-1 py-1.5 text-xs text-white rounded-md font-medium transition-colors flex items-center justify-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed ${confirmClass}`}
          >
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Pre-built icon helpers ─────────────────────────────────────── */

export const FileIcon = ({ className = 'w-7 h-7 text-indigo-600' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

export const RegenerateIcon = ({ className = 'w-7 h-7 text-amber-500' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

export const SendIcon = ({ className = 'w-7 h-7 text-blue-600' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
  </svg>
);

export const SendBackIcon = ({ className = 'w-7 h-7 text-red-500' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
  </svg>
);

export const ApproveIcon = ({ className = 'w-7 h-7 text-green-600' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

export const RejectIcon = ({ className = 'w-7 h-7 text-red-500' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

export const AccountsIcon = ({ className = 'w-7 h-7 text-green-600' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
  </svg>
);

export default ConfirmModal;